import React, { useState, useEffect, useContext, useId } from "react";
import {
  Alert,
  Button,
  Container,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";

import { DataContext } from "../../../context/DataContext";
import { tienePermiso } from "../../../helpers/permisos";
import { getToken } from "../../../api/Account";
import {
  addPeriodicidadAsync,
  deletePeriodicidadAsync,
  getPeriodicidadListAsync,
  updatePeriodicidadAsync,
} from "../../../api/Periodicidad";
import {
  CargandoTabla,
  EstadoError,
  EstadoVacio,
  PageHeader,
  useAviso,
} from "../../../components/base";
import {
  DialogoFormulario,
  encabezadoTabla,
  useCargaLocal,
  useConfirmacion,
  useEnvio,
  useEscritura,
} from "../comunes";

const entero = (valor) => /^\d+$/.test(String(valor ?? "").trim());

/**
 * Valida una periodicidad. Los días tienen que ser enteros mayores que cero: antes "mayor que 0"
 * comparaba texto con número y nunca se cumplía, así que se podía guardar 0.
 * @returns {Object<string, string>} errores por campo.
 */
export const validarPeriodicidad = (datos) => {
  const errores = {};
  if (String(datos.description ?? "").trim() === "") errores.description = "Escriba el nombre";
  for (const campo of ["cantidadDias", "cuotaDias"]) {
    const v = String(datos[campo] ?? "").trim();
    if (v === "") errores[campo] = "Escriba los días";
    else if (!entero(v) || Number(v) <= 0) errores[campo] = "Debe ser un número entero mayor que 0";
  }
  return errores;
};

const VACIA = { description: "", cantidadDias: "", cuotaDias: "" };

/** Alta (sin `periodicidad`) o cambio de una periodicidad. */
const DialogoPeriodicidad = ({ periodicidad, onCerrar, onGuardado }) => {
  const token = getToken();
  const escribir = useEscritura();
  const { guardando, enviar } = useEnvio();
  const id = useId();
  const esAlta = !periodicidad;
  const original = esAlta
    ? VACIA
    : {
        description: periodicidad.description ?? "",
        cantidadDias: String(periodicidad.cantidadDias ?? ""),
        cuotaDias: String(periodicidad.cuotaDias ?? ""),
      };
  const [datos, setDatos] = useState(original);
  const [intentado, setIntentado] = useState(false);

  const hayCambios = ["description", "cantidadDias", "cuotaDias"].some(
    (c) => String(datos[c]).trim() !== String(original[c]).trim()
  );
  const errores = intentado ? validarPeriodicidad(datos) : {};
  if (intentado && !esAlta && !hayCambios) errores.description = "No hay cambios que guardar";

  const campo = (nombre, valor) =>
    setDatos((actual) => ({ ...actual, [nombre]: valor }));

  const guardar = () =>
    enviar(async () => {
      setIntentado(true);
      if (Object.keys(validarPeriodicidad(datos)).length > 0 || (!esAlta && !hayCambios)) return;
      const cuerpo = {
        description: datos.description.trim(),
        cantidadDias: datos.cantidadDias.trim(),
        cuotaDias: datos.cuotaDias.trim(),
      };
      const ok = await escribir(
        () =>
          esAlta
            ? addPeriodicidadAsync(token, cuerpo)
            : updatePeriodicidadAsync(token, { id: periodicidad.id, ...cuerpo }),
        esAlta ? "No se pudo agregar la periodicidad" : "No se pudo cambiar la periodicidad"
      );
      if (ok) onGuardado(cuerpo.description);
    });

  const numero = (nombre, etiqueta, ayuda) => (
    <TextField
      fullWidth
      required
      id={`${id}-${nombre}`}
      label={etiqueta}
      value={datos[nombre]}
      onChange={(e) => {
        if (/^\d*$/.test(e.target.value)) campo(nombre, e.target.value);
      }}
      error={Boolean(errores[nombre])}
      helperText={errores[nombre] || ayuda}
      disabled={guardando}
      inputProps={{ inputMode: "numeric", autoComplete: "off" }}
    />
  );

  return (
    <DialogoFormulario
      titulo={esAlta ? "Nueva periodicidad" : `Cambiar ${original.description}`}
      onCerrar={onCerrar}
      hayCambios={hayCambios}
      guardando={guardando}
      onEnviar={guardar}
      textoEnviar={esAlta ? "Agregar periodicidad" : "Guardar cambios"}
    >
      {!esAlta ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Los créditos que ya usan esta periodicidad toman los días nuevos al regenerar su plan o al
          recibir abonos nominales.
        </Alert>
      ) : null}
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            autoFocus
            id={`${id}-description`}
            label="Nombre"
            value={datos.description}
            onChange={(e) => campo("description", e.target.value.toUpperCase())}
            error={Boolean(errores.description)}
            helperText={errores.description || "Por ejemplo QUINCENAL o DIARIO 5 DIAS"}
            disabled={guardando}
            inputProps={{ autoComplete: "off" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          {numero(
            "cuotaDias",
            "Cobro cada (días)",
            "Cada cuántos días se cobra una cuota: MENSUAL 30, QUINCENAL 15, SEMANAL 7, DIARIO 1"
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {numero(
            "cantidadDias",
            "Días por mes",
            "Días que suman un mes con este cobro, para el interés mensual: MENSUAL 30, SEMANAL 28, DIARIO 5 DIAS 20"
          )}
        </Grid>
      </Grid>
    </DialogoFormulario>
  );
};

const PeriodicidadList = () => {
  const token = getToken();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir: pedirConfirmacion, dialogo: confirmacion } = useConfirmacion();

  const [lista, setLista] = useState([]);
  const [recargar, setRecargar] = useState(false);
  const [menu, setMenu] = useState({ ancla: null, fila: null });
  const [dialogo, setDialogo] = useState(null);

  const puedeAgregar = tienePermiso(access, "CONFIG CREATE");
  const puedeEditar = tienePermiso(access, "CONFIG UPDATE");
  const puedeBorrar = tienePermiso(access, "CONFIG DELETE");

  const { cargando, error, cargar } = useCargaLocal(() => getPeriodicidadListAsync(token));

  useEffect(() => {
    let vigente = true;
    (async () => {
      const result = await cargar();
      if (vigente && result) setLista(result.data.result || []);
    })();
    return () => {
      vigente = false;
    };
  }, [recargar]);

  const recargarLista = () => setRecargar((actual) => !actual);

  const borrar = async (fila) => {
    const ok = await escribir(() => deletePeriodicidadAsync(token, fila.id), "No se pudo borrar la periodicidad");
    if (!ok) return;
    recargarLista();
    aviso.exito(`Periodicidad ${fila.description} borrada`);
  };

  const pedirBorrar = (fila) =>
    pedirConfirmacion({
      titulo: "Borrar periodicidad",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se borrará ${fila.description}: ya no se podrá elegir al crear créditos.`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Si algún crédito o plazo la usa, el sistema no dejará borrarla.
          </Typography>
        </>
      ),
      textoConfirmar: "Borrar periodicidad",
      peligro: true,
      onConfirmar: () => borrar(fila),
    });

  const botonAgregar = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogo({ periodicidad: null })}>
      Agregar periodicidad
    </Button>
  );

  const hayAcciones = puedeEditar || puedeBorrar;

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={6} columnas={4} etiqueta="Cargando periodicidades…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={error === "conexion" ? "No se pudo conectar con el servidor" : "No se pudieron cargar las periodicidades"}
        onReintentar={recargarLista}
      />
    );
  } else if (lista.length === 0) {
    contenido = (
      <EstadoVacio
        titulo="Todavía no hay periodicidades"
        descripcion="Son los ritmos de cobro de un crédito: mensual, quincenal, semanal, diario…"
        accion={puedeAgregar ? botonAgregar : undefined}
      />
    );
  } else {
    contenido = (
      <TableContainer>
        <Table aria-label="Periodicidades" sx={{ "& .MuiTableCell-root": { px: { xs: 1, sm: 2 } } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={encabezadoTabla}>Periodicidad</TableCell>
              <TableCell sx={{ ...encabezadoTabla, textAlign: "right", display: { xs: "none", sm: "table-cell" } }}>
                Cobro cada
              </TableCell>
              <TableCell sx={{ ...encabezadoTabla, textAlign: "right", display: { xs: "none", sm: "table-cell" } }}>
                Días por mes
              </TableCell>
              {hayAcciones ? <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>Acciones</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {lista.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell component="th" scope="row">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {row.description}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: { xs: "block", sm: "none" } }}>
                    {`Cobro cada ${row.cuotaDias} ${Number(row.cuotaDias) === 1 ? "día" : "días"} · ${row.cantidadDias} días por mes`}
                  </Typography>
                </TableCell>
                <TableCell sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", display: { xs: "none", sm: "table-cell" } }}>
                  {`${row.cuotaDias} ${Number(row.cuotaDias) === 1 ? "día" : "días"}`}
                </TableCell>
                <TableCell sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", display: { xs: "none", sm: "table-cell" } }}>
                  {row.cantidadDias}
                </TableCell>
                {hayAcciones ? (
                  <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {puedeEditar ? (
                      <Tooltip title="Cambiar periodicidad">
                        <IconButton
                          aria-label={`Cambiar ${row.description}`}
                          onClick={() => setDialogo({ periodicidad: row })}
                          sx={{ color: "primary.main" }}
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {puedeBorrar ? (
                      <Tooltip title="Más acciones">
                        <IconButton
                          aria-label={`Más acciones de ${row.description}`}
                          aria-haspopup="menu"
                          onClick={(e) => setMenu({ ancla: e.currentTarget, fila: row })}
                          sx={{ color: "text.secondary" }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Periodicidades"
        subtitulo="Ritmos de cobro que se eligen al crear un crédito"
        icono={<FontAwesomeIcon icon={faCalendarDays} />}
        acciones={puedeAgregar ? botonAgregar : undefined}
      />
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>{contenido}</Paper>

      <Menu
        anchorEl={menu.ancla}
        open={Boolean(menu.ancla)}
        onClose={() => setMenu((actual) => ({ ...actual, ancla: null }))}
      >
        <MenuItem
          onClick={() => {
            const fila = menu.fila;
            setMenu((actual) => ({ ...actual, ancla: null }));
            pedirBorrar(fila);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <DeleteOutlineIcon />
          </ListItemIcon>
          <ListItemText>Borrar periodicidad</ListItemText>
        </MenuItem>
      </Menu>

      {dialogo ? (
        <DialogoPeriodicidad
          periodicidad={dialogo.periodicidad}
          onCerrar={() => setDialogo(null)}
          onGuardado={(nombre) => {
            aviso.exito(dialogo.periodicidad ? `Periodicidad ${nombre} actualizada` : `Periodicidad ${nombre} agregada`);
            setDialogo(null);
            recargarLista();
          }}
        />
      ) : null}

      {confirmacion}
    </Container>
  );
};

export default PeriodicidadList;
